import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MessageSquare, Clock, Users, AlertTriangle, ExternalLink, BookOpen, ArrowRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

const HipChatHistory = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": { "@type": "WebPage", "@id": "https://csw.live/hipchat/history/" },
    "headline": "HipChat: The Pioneer of Team Chat That Lost to Slack",
    "description": "The history of HipChat, Atlassian's team chat app that pioneered workplace messaging before being discontinued in 2019 after partnering with Slack.",
    "keywords": "HipChat history, HipChat shutdown, Atlassian, Slack alternative, team chat history",
    "author": { "@type": "Organization", "name": "CSW Team", "url": "https://csw.live" },
    "publisher": { "@type": "Organization", "name": "CSW - Chill Study Work", "url": "https://csw.live" },
    "datePublished": "2025-01-11",
    "dateModified": "2025-01-11"
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="HipChat History - The Rise and Fall of Team Chat Pioneer"
        description="A retrospective on HipChat - Atlassian's team chat app that pioneered workplace messaging before being discontinued in 2019 after partnering with Slack."
        keywords="hipchat history, hipchat shutdown, atlassian, slack acquisition, team chat pioneer"
        canonicalUrl="/hipchat/history/"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Header />
      
      <main className="pt-20">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
          <div className="container mx-auto px-4 relative z-10">
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li>/</li>
                <li><Link to="/hipchat/" className="hover:text-primary transition-colors">HipChat</Link></li>
                <li>/</li>
                <li className="text-foreground">History</li>
              </ol>
            </nav>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Calendar className="w-4 h-4" />
                A Retrospective • 2010-2019
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                The HipChat Story
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                The pioneer of team chat that helped define modern workplace communication.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-muted-foreground">
                    HipChat was launched in 2010 by RPI classmates Chris Rivers, Garret Heaton, and Pete Curley as a private group chat and instant messaging service. The startup was quickly noticed for its user-friendly approach to team communication.
                  </p>
                  <p className="text-lg leading-relaxed text-muted-foreground mt-4">
                    <Link to="/hipchat/" className="text-primary hover:underline font-medium">Looking for a HipChat alternative?</Link>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">History & Acquisition</h2>
              </div>
              
              <div className="prose prose-lg max-w-none space-y-6">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  HipChat supported persistent chat rooms, one-on-one messaging, searchable history, file sharing, and integrations with tools like GitHub and Heroku. <span className="text-foreground font-semibold">Atlassian acquired HipChat in 2012</span>, integrating it with their suite (Jira, Confluence), and by 2014 offered a freemium model to spur adoption.
                </p>
                
                <p className="text-muted-foreground text-lg leading-relaxed">
                  For a time, HipChat was popular among tech companies and dev teams as "Yammer for the masses," essentially an early Slack alternative. Its desktop and mobile apps enabled office and remote teams to collaborate in real-time, making it a pioneer in modern workplace chat.
                </p>

                <Card className="border-border/50 my-8">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4">Key Features</h3>
                    <ul className="grid md:grid-cols-2 gap-3 text-muted-foreground">
                      <li className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Persistent chat rooms</li>
                      <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-primary" /> One-on-one messaging</li>
                      <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> Searchable history</li>
                      <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-primary" /> File sharing (5GB free)</li>
                    </ul>
                  </CardContent>
                </Card>

                <a href="https://en.wikipedia.org/wiki/HipChat" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors">
                  <ExternalLink className="w-4 h-4" />
                  Wikipedia: HipChat
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-destructive/10">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">The Shutdown</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  Despite its early lead, HipChat eventually met stiff competition from newcomer <span className="text-foreground font-semibold">Slack after 2013</span>. Slack's rapid growth outpaced HipChat's traction.
                </p>
                
                <p>
                  Atlassian attempted a comeback by launching <span className="text-foreground font-semibold">Stride</span> in September 2017—an overhaul with added features like built-in video conferencing and task tracking. However, Stride also struggled to win back teams that had migrated to Slack.
                </p>

                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl mb-4">The End</p>
                    <p className="text-muted-foreground">
                      In <span className="font-semibold">July 2018</span>, Atlassian announced a partnership with Slack and the discontinuation of both HipChat and Stride. Slack effectively acquired the IP and user base. HipChat's cloud service was shut down on <span className="font-semibold">February 15, 2019</span>.
                    </p>
                  </CardContent>
                </Card>

                <p>
                  Atlassian's reasoning was pragmatic—rather than split the market, they joined forces with Slack, noting <span className="italic">"if you can't beat 'em, join 'em."</span>
                </p>

                <div className="flex flex-wrap gap-3">
                  <a href="https://techcrunch.com/2018/07/26/atlassians-hipchat-and-stride-to-be-discontinued-with-slack-buying-up-the-ip/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors">
                    <ExternalLink className="w-4 h-4" />
                    TechCrunch: HipChat Discontinued
                  </a>
                  <a href="https://www.theverge.com/2018/7/26/17619482/slack-hipchat-acquisition-stride-atlassian-partnership-microsoft-teams-competition" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm transition-colors">
                    <ExternalLink className="w-4 h-4" />
                    The Verge: Slack Buys HipChat
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Looking for a <span className="text-gradient">HipChat Alternative?</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                CSW offers free virtual collaboration rooms where you can work alongside others, stay focused, and be productive.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild>
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Try CSW Free <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/hipchat/">View HipChat Alternative</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HipChatHistory;
